import { useState, useEffect } from "react";
import {
  useGetChatCommunicationQuery,
  useAnalyzeChatConversationMutation,
  useSuggestImprovementMutation,
  useApplyImprovementMutation,
  useGetChatHistoryQuery,
  useGetVendorQuestionsQuery,
} from "apis/chatCommunication";
import { useGetUserQuery } from "apis/user";
import { useGetSuburbQuery } from "apis/suburb";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import {
  LuArrowLeft,
  LuCalendar,
  LuMessageSquare,
  LuSearch,
  LuThumbsDown,
  LuThumbsUp,
  LuUser,
  LuWand2,
} from "react-icons/lu";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import moment from "moment";
import EditableField from "components/ui/EditableField";
function getNearestDateToNow(history) {
  if (!history || history.length === 0) return 0;

  const first = new Date(history[0]?.createdAt || 0).getTime();
  const last = new Date(history[history.length - 1]?.createdAt || 0).getTime();
  const now = Date.now();

  // Return whichever message is closer to "now"
  return Math.abs(now - first) < Math.abs(now - last) ? first : last;
}
export default function ChatCommunication() {
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSentiment, setFilterSentiment] = useState("all");
  const [showOnlyVendorQuestions, setShowOnlyVendorQuestions] = useState(false);
  const [activeTab, setActiveTab] = useState("messages");
  const [chatTypeFilter, setChatTypeFilter] = useState("vendor");
  const [userTypeFilter, setUserTypeFilter] = useState("buyer");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(80);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userDateFilter, setUserDateFilter] = useState("all"); // all, today, week, month
  const [chatDateFilter, setChatDateFilter] = useState("all"); // all, today, week, month
  const [suburbFilter, setSuburbFilter] = useState(""); // suburb filter for vendor/homeowner
  const [vendorQuestionsPage, setVendorQuestionsPage] = useState(1);
  // Map UI filter to API type
  const getApiChatType = (filter) => {
    const typeMap = {
      vendor: "vendorGroup",
      buyer: "singleBuyerGroup",
      purchaser: "buyerGroup",
      community: "communityAlert",
      agent: "agentAssistant",
      user: null, // No API call for user tab
      "vendorQuestions": null, // Uses separate vendor questions API
    };
    return typeMap[filter];
  };
  
  const apiChatType = getApiChatType(chatTypeFilter);
  const {
    data: chats,
    pagination,
    isPending: isChatsPending,
    isError: isChatsError,
    error: chatsError,
  } = useGetChatCommunicationQuery(apiChatType, currentPage, pageLimit);
  const {
    data: users,
    isLoading: isUsersLoading,
    isError: isUsersError,
    error: usersError,
  } = useGetUserQuery({ hasApp: true });

  // Clear selected chat and reset page when switching tabs
  useEffect(() => {
    setSelectedChat(null);
    setCurrentPage(1);
  }, [chatTypeFilter]);

  const { data: suburbs, isPending: isSuburbsPending } = useGetSuburbQuery();
  const { mutate: analyzeChat, isPending: isAnalyzing } =
    useAnalyzeChatConversationMutation();
  const { mutate: suggestImprovement, isPending: isSuggesting } =
    useSuggestImprovementMutation();
  const { mutate: applyImprovement, isPending: isApplying } =
    useApplyImprovementMutation();
  
  const {
    data: vendorQuestions,
    pagination: vendorQuestionsPagination,
    isPending: isVendorQuestionsPending,
  } = useGetVendorQuestionsQuery(vendorQuestionsPage, 100);

  const selectedChatObj = chats.find((c) => c._id === selectedChat);
  const selectedChatDbId = selectedChatObj?.chatId?._id;
  const { data: fullHistory, isPending: isHistoryPending } =
    useGetChatHistoryQuery(selectedChatDbId);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showImprovementDetailModal, setShowImprovementDetailModal] = useState(false);
  const [selectedImprovementCard, setSelectedImprovementCard] = useState(null);
  const [suggestionData, setSuggestionData] = useState(null);
  const [editedSuggestedContent, setEditedSuggestedContent] = useState("");
  const [selectedMessageForSuggestion, setSelectedMessageForSuggestion] =
    useState(null);
  const [selectedLogForSuggestion, setSelectedLogForSuggestion] = useState(null);

  // Keep a local, editable copy of the suggested content so users can tweak it
  useEffect(() => {
    if (suggestionData) {
      setEditedSuggestedContent(suggestionData.suggestedContent ?? "");
    } else {
      setEditedSuggestedContent("");
    }
  }, [suggestionData]);
  

  const filteredChats = chats
    .filter((chat) => {
      // API already filters by chat type, so we only need to filter by search and sentiment
      const matchesSearch =
        searchQuery === "" ||
        chat?.chatId?.propertyId?.address
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        chat?.chatId?.name?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesSentiment =
        filterSentiment === "all" ||
        (filterSentiment === "positive" &&
          chat.overallSentiment === "positive") ||
        (filterSentiment === "negative" &&
          chat.overallSentiment === "negative") ||
        chat?.summary ||
        chat?.metrics?.negativeCount > 0 ||
        chat?.metrics?.issuesIdentified > 0 ||
        filterSentiment === "needs-review" ||
        (chat.overallSentiment === "negative" &&
          chat.analysisResults?.promptSuggestions?.length > 0);

      // Date filter logic for chats
      let matchesDateFilter = true;
      if (chatDateFilter !== "all") {
        const chatDate = new Date(chat.updatedAt || chat.createdAt).getTime();
        if (chatDate) {
          const now = moment();
          const chatMoment = moment(chatDate);
          if (chatDateFilter === "today") {
            matchesDateFilter = chatMoment.isSame(now, "day");
          } else if (chatDateFilter === "week") {
            matchesDateFilter = chatMoment.isAfter(
              now.clone().subtract(7, "days"),
            );
          } else if (chatDateFilter === "month") {
            matchesDateFilter = chatMoment.isAfter(
              now.clone().subtract(30, "days"),
            );
          }
        } else {
          matchesDateFilter = false;
        }
      }

      return matchesSearch && matchesSentiment && matchesDateFilter;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      return dateB - dateA; // newest first
    });
  const isSameUser = (user1, user2) => {
    if (user1._id === user2._id) return false;
    const email1 = user1.email?.toLowerCase();
    const email2 = user2.email?.toLowerCase();
    const phone1 = user1.mobile || user1.phone;
    const phone2 = user2.mobile || user2.phone;
    const name1 = user1.name?.toLowerCase();
    const name2 = user2.name?.toLowerCase();

    return (
      (email1 && email2 && email1 === email2) ||
      (phone1 && phone2 && phone1 === phone2) ||
      (name1 && name2 && name1 === name2) ||
      (name1?.toLowerCase()?.includes("joyce") &&
        name2?.toLowerCase()?.includes("joyce"))
    );
  };

  const dateFilteredUsers = users.filter((user) => {
    if (userDateFilter === "all") return true;

    const now = moment();
    let dateToCheck = null;

    if (userDateFilter.startsWith("installed")) {
      dateToCheck = user.appInstallDate;
    } else if (userDateFilter.startsWith("active")) {
      dateToCheck = user.lastActiveDate;
    }

    if (!dateToCheck) return false;

    const userDate = moment(dateToCheck);
    if (
      userDateFilter === "installedToday" ||
      userDateFilter === "activeToday"
    ) {
      return userDate.isSame(now, "day");
    } else if (
      userDateFilter === "installedWeek" ||
      userDateFilter === "activeWeek"
    ) {
      return userDate.isAfter(moment().subtract(7, "days"));
    } else if (
      userDateFilter === "installedMonth" ||
      userDateFilter === "activeMonth"
    ) {
      return userDate.isAfter(moment().subtract(30, "days"));
    }
    return true;
  });

  const agentUsers = users.filter((user) => user.type === "Agent");
  const vendorUsers = dateFilteredUsers.filter(
    (user) => user.type === "Vendor",
  );
  const buyerUsers = dateFilteredUsers.filter((user) => user.type === "Buyer");

  const homeownerUsers = vendorUsers.filter((vendor) => {
    if (agentUsers.some((agent) => isSameUser(vendor, agent))) {
      return false;
    }

    if (!vendor.chats || vendor.chats.length === 0) return true;
    const hasVendorGroupChat = vendor.chats.some(
      (chat) => chat.metadata?.type === "vendorGroup",
    );

    return !hasVendorGroupChat;
  });
  const homeownerIds = new Set(homeownerUsers.map((u) => u._id));
  const actualVendorUsers = vendorUsers.filter((v) => !homeownerIds.has(v._id));

  const suburbFilteredVendors = suburbFilter
    ? actualVendorUsers.filter((user) => {
        if (!user.chats || user.chats.length === 0) return false;
        return user.chats.some(
          (chat) =>
            chat.name?.toLowerCase().includes(suburbFilter.toLowerCase()) ||
            chat.metadata?.suburb?.toLowerCase() === suburbFilter.toLowerCase(),
        );
      })
    : actualVendorUsers;

  const suburbFilteredHomeowners = suburbFilter
    ? homeownerUsers.filter((user) => {
        if (!user.chats || user.chats.length === 0) return false;
        return user.chats.some(
          (chat) =>
            chat.name?.toLowerCase().includes(suburbFilter.toLowerCase()) ||
            chat.metadata?.suburb?.toLowerCase() === suburbFilter.toLowerCase(),
        );
      })
    : homeownerUsers;

  const buyerCount = buyerUsers.filter(
    (user) =>
      !agentUsers.some((agent) => isSameUser(user, agent)) &&
      !vendorUsers.some((vendor) => isSameUser(user, vendor)),
  ).length;
  const vendorCount = suburbFilteredVendors.filter(
    (user) => !agentUsers.some((agent) => isSameUser(user, agent)),
  ).length;
  const homeownerCount = suburbFilteredHomeowners.length;
  const agentCount = agentUsers.length;

  const filteredUsers = dateFilteredUsers
    .filter((user) => {
      if (
        (userTypeFilter === "vendor" || userTypeFilter === "buyer") &&
        agentUsers.some((agent) => isSameUser(user, agent))
      ) {
        return false;
      }
      if (
        userTypeFilter === "buyer" &&
        vendorUsers.some((vendor) => isSameUser(user, vendor))
      ) {
        return false;
      }

      const matchesUserType =
        userTypeFilter === "buyer"
          ? user.type === "Buyer"
          : userTypeFilter === "vendor"
            ? suburbFilteredVendors.some((v) => v._id === user._id)
            : userTypeFilter === "homeowner"
              ? suburbFilteredHomeowners.some((ho) => ho._id === user._id)
              : userTypeFilter === "agent"
                ? user.type === "Agent"
                : true;

      const matchesSearch =
        userSearchQuery === "" ||
        user?.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        user?.email?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        user?.mobile?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        user?.phone?.toLowerCase().includes(userSearchQuery.toLowerCase());

      return matchesUserType && matchesSearch;
    })
    .sort((a, b) => {
      const dateA = new Date(b.lastActiveDate || 0).getTime();
      const dateB = new Date(a.lastActiveDate || 0).getTime();
      return dateA - dateB;
    });

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getParticipantNames = (participants) => {
    if (!participants || participants.length === 0) return "";
    return participants
      .filter((p) => p.userId?.name)
      .map((p) => p.userId.name)
      .join(", ");
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "high":
        return "bg-green-600 text-white";
      case "low":
        return "bg-red-600 text-white";
      default:
        return "bg-mediumgray text-white";
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "high":
        return <LuThumbsUp className="w-3 h-3" />;
      case "low":
        return <LuThumbsDown className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const startAnalysis = () => {
    const chat = chats.find((c) => c._id === selectedChat);
    analyzeChat(
      {
        chatId: chat.chatId._id,
        logId: chat._id,
        promptName: chat?.chatId?.name?.toLowerCase()?.includes("community")
          ? "SANDY_VENDOR_GROUP_ALERTS"
          : "SANDY_VENDOR_GROUP",
      },
      {
        onSuccess: () => {
          showToast("success", "Analysis complete.");
        },
        onError: () => {
          showToast("error", "Failed to analyse chat. Please try again.");
        },
      },
    );
  };

  const handleSuggestImprovement = (chatSentimentLogId, messageId) => {
    setSelectedMessageForSuggestion(messageId);
    setSelectedLogForSuggestion(chatSentimentLogId || null);
    setSuggestionData(null);
    setShowSuggestionModal(true);
    const payload = chatSentimentLogId
      ? { chatSentimentLogId, messageId }
      : { messageId };
    suggestImprovement(payload, {
      onSuccess: (res) => {
        setSuggestionData(res?.data?.data ?? null);
      },
      onError: () => {
        showToast("error", "Failed to get suggestion. Please try again.");
        setShowSuggestionModal(false);
      },
    });
  };

  const handleApplyImprovement = () => {
    if (!suggestionData) return;
    const { triggerSource, targetField } = suggestionData || {};
    const chat = chats.find((c) => c._id === selectedChat);
    applyImprovement(
      {
        sourceModel: triggerSource?.sourceModel,
        sourceName: triggerSource?.sourceName,
        targetField,
        // Use the edited content if the user changed it in the modal
        newContent: editedSuggestedContent ?? suggestionData?.suggestedContent ?? "",
        chatSentimentLogId: selectedLogForSuggestion ?? chat?._id,
        messageId: selectedMessageForSuggestion,
        suggestionText: JSON.stringify(suggestionData?.improvementPoints || []),
      },
      {
        onSuccess: () => {
          showToast("success", "Improvement applied successfully.");
          setShowSuggestionModal(false);
          setSuggestionData(null);
          setSelectedMessageForSuggestion(null);
          setSelectedLogForSuggestion(null);
        },
        onError: () => {
          showToast("error", "Failed to apply improvement. Please try again.");
        },
      },
    );
  };

  if (
    (isChatsPending && chatTypeFilter !== "user") ||
    (chatTypeFilter === "user" && isUsersLoading)
  ) {
    return <PageLoader />;
  }

  return (
    <>
      <div className="mb-4 flex overflow-x-auto whitespace-nowrap box-scrollbar scroll-smooth my-scroll-container">
        <div className="flex gap-2">
          <Button
            className={`flex justify-center items-center gap-1 ${
              chatTypeFilter === "vendor" ? "black-button" : "gray-button"
            }`}
            onClick={() => setChatTypeFilter("vendor")}
          >
            Vendor
          </Button>
          <Button
            className={`flex justify-center items-center gap-1 ${
              chatTypeFilter === "buyer" ? "black-button" : "gray-button"
            }`}
            onClick={() => setChatTypeFilter("buyer")}
          >
            Buyer
          </Button>
          <Button
            className={`flex justify-center items-center gap-1 ${
              chatTypeFilter === "purchaser" ? "black-button" : "gray-button"
            }`}
            onClick={() => setChatTypeFilter("purchaser")}
          >
            Purchaser
          </Button>
          <Button
            className={`flex justify-center items-center gap-1 ${
              chatTypeFilter === "community" ? "black-button" : "gray-button"
            }`}
            onClick={() => setChatTypeFilter("community")}
          >
            Community
          </Button>
          <Button
            className={`flex justify-center items-center gap-1 ${
              chatTypeFilter === "agent" ? "black-button" : "gray-button"
            }`}
            onClick={() => setChatTypeFilter("agent")}
          >
            Agent
          </Button>
          <Button
            className={`flex justify-center items-center gap-1 ${
              chatTypeFilter === "user" ? "black-button" : "gray-button"
            }`}
            onClick={() => setChatTypeFilter("user")}
          >
            User
          </Button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-6">
        {chatTypeFilter === "user" ? (
          <div className="w-full bg-white rounded border">
            <div className="p-5 border-b">
              <h3>
                App Users{" "}
                {chatTypeFilter === "user" &&
                  `(${vendorCount + buyerCount + homeownerCount + agentCount})`}
              </h3>
              <div className="mt-4 relative">
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  className="w-full form-input border rounded"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                />
                <LuSearch className="absolute right-3 top-3 h-5 w-5 text-lightgray" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  className={`flex justify-center items-center gap-1 ${
                    userTypeFilter === "buyer" ? "black-button" : "gray-button"
                  }`}
                  onClick={() => setUserTypeFilter("buyer")}
                >
                  Buyer ({buyerCount})
                </Button>
                <Button
                  className={`flex justify-center items-center gap-1 ${
                    userTypeFilter === "vendor" ? "black-button" : "gray-button"
                  }`}
                  onClick={() => setUserTypeFilter("vendor")}
                >
                  Vendor ({vendorCount})
                </Button>
                <Button
                  className={`flex justify-center items-center gap-1 ${
                    userTypeFilter === "homeowner"
                      ? "black-button"
                      : "gray-button"
                  }`}
                  onClick={() => setUserTypeFilter("homeowner")}
                >
                  Home Owner ({homeownerCount})
                </Button>
                <Button
                  className={`flex justify-center items-center gap-1 ${
                    userTypeFilter === "agent" ? "black-button" : "gray-button"
                  }`}
                  onClick={() => setUserTypeFilter("agent")}
                >
                  Agent ({agentCount})
                </Button>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium mb-2 block">
                  Filter by Date
                </label>
                <select
                  className="w-full form-select border rounded"
                  value={userDateFilter}
                  onChange={(e) => setUserDateFilter(e.target.value)}
                >
                  <option value="all">All Users</option>
                  <optgroup label="Installed">
                    <option value="installedToday">Installed Today</option>
                    <option value="installedWeek">Installed This Week</option>
                    <option value="installedMonth">Installed This Month</option>
                  </optgroup>
                  <optgroup label="Last Active">
                    <option value="activeToday">Active Today</option>
                    <option value="activeWeek">Active This Week</option>
                    <option value="activeMonth">Active This Month</option>
                  </optgroup>
                </select>
              </div>
              {(userTypeFilter === "vendor" ||
                userTypeFilter === "homeowner") && (
                <div className="mt-4">
                  <label className="text-sm font-medium mb-2 block">
                    Filter by Suburb
                  </label>
                  <select
                    className="w-full form-select border rounded"
                    value={suburbFilter}
                    onChange={(e) => setSuburbFilter(e.target.value)}
                    disabled={isSuburbsPending}
                  >
                    <option value="">All Suburbs</option>
                    {suburbs?.map((suburb) => (
                      <option key={suburb._id} value={suburb.suburb}>
                        {suburb.suburb}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="divide-y divide-lightgray max-h-[calc(100vh-14rem)] overflow-y-auto">
              {isUsersLoading ? (
                <div className="p-6 text-center">
                  <PageLoader />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-6 text-center text-darkgray">
                  <LuUser className="w-8 h-8 text-darkgray mx-auto mb-2" />
                  <p>No users found</p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className="p-4 hover:bg-lightergray cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-darkgray flex items-center justify-center text-white">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm truncate">
                          {user.name}
                        </h4>
                        <p className="text-xs text-darkgray truncate">
                          {user.email}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-darkgray">
                            {user.mobile || user.phone || "No phone"}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-lightgray rounded">
                            {userTypeFilter === "homeowner"
                              ? "Home Owner"
                              : user.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {user?.appInstallDate && (
                            <span className="text-xs text-darkgray">
                              Installed:{" "}
                              {moment(user.appInstallDate).format(
                                "MMM D, YYYY",
                              )}
                            </span>
                          )}
                          {user?.lastActiveDate && (
                            <span className="text-xs text-darkgray">
                              Last Active:{" "}
                              {moment(user.lastActiveDate).fromNow()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div
            className={`bg-white rounded border transition-all duration-300 ${
              filterSentiment === "vendorQuestions"
                ? "w-full md:w-2/3"
                : `w-full md:w-1/3 ${selectedChat ? "hidden md:block" : ""}`
            }`}
          >
            <div className="p-5 border-b">
              <h3>
                Chat Conversations{" "}
                {chatTypeFilter === "vendor" && pagination
                  ? pagination.total
                  : filteredChats.length}
              </h3>
              <p>{pagination ? `for Page ${pagination.page}` : ""}</p>
              <div className="mt-4 relative">
                <input
                  type="text"
                  placeholder="Search by property or chat name..."
                  className="w-full form-input border rounded"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <LuSearch className="absolute right-3 top-3 h-5 w-5 text-lightgray" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  className={`flex justify-center items-center gap-1 ${
                    filterSentiment === "all" ? "black-button" : "gray-button"
                  }`}
                  onClick={() => setFilterSentiment("all")}
                >
                  All
                </Button>
                <Button
                  className={`flex justify-center gray-button items-center gap-1 ${
                    filterSentiment === "positive"
                      ? "!bg-green-600 text-white"
                      : "gray-button"
                  }`}
                  onClick={() => setFilterSentiment("positive")}
                >
                  <LuThumbsUp className="w-3.5 h-3.5" /> Positive
                </Button>
                <Button
                  className={`flex justify-center gray-button items-center gap-1 ${
                    filterSentiment === "negative"
                      ? "!bg-red-600 text-white"
                      : "gray-button"
                  }`}
                  onClick={() => setFilterSentiment("negative")}
                >
                  <LuThumbsDown className="w-3.5 h-3.5" /> Negative
                </Button>
                <Button
                  className={`flex justify-center gray-button items-center gap-1 ${
                    filterSentiment === "needs-review"
                      ? "!bg-orange-500 text-white"
                      : "gray-button"
                  }`}
                  onClick={() => setFilterSentiment("needs-review")}
                >
                  Needs Review
                </Button>
                {chatTypeFilter === "vendor" && (
                  <Button
                    className={`flex justify-center gray-button items-center gap-1 ${
                      filterSentiment === "vendorQuestions"
                        ? "!bg-red-600 text-white"
                        : "gray-button"
                    }`}
                    onClick={() => setFilterSentiment(filterSentiment === "vendorQuestions" ? "all" : "vendorQuestions")}
                  >
                    Vendor Questions
                  </Button>
                )}
              </div>
              {filterSentiment !== "vendorQuestions" && <div className="mt-4">
                <label className="text-sm font-medium mb-2 block">
                  Filter by Date
                </label>
                <select
                  className="w-full form-select border rounded"
                  value={chatDateFilter}
                  onChange={(e) => setChatDateFilter(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>}
            </div>
            {filterSentiment === "vendorQuestions" ? null : (
              <>
            <div className="divide-y divide-lightgray max-h-[calc(100vh-14rem)] overflow-y-auto">
              {filteredChats.length === 0 ? (
                <div className="p-6 text-center text-darkgray">
                  <LuMessageSquare className="w-8 h-8 text-darkgray mx-auto mb-2" />
                  <p>No chats found</p>
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <div
                    key={chat._id}
                    className={`p-4 hover:bg-lightgray cursor-pointer transition-colors duration-200 ${
                      selectedChat === chat._id
                        ? "bg-lightgray border-l-4 border-black"
                        : ""
                    }`}
                    onClick={() => { setSelectedChat(chat._id); setShowOnlyVendorQuestions(false); }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-black">
                          {chat.chatId?.name ||
                            chat.chatId?.propertyId?.address}
                        </p>
                        {chat.chatId?.participants && (
                          <p className="text-xs text-darkgray mt-1">
                            {getParticipantNames(chat.chatId.participants)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-darkgray flex items-center gap-1">
                          latest Message
                        </span>
                        <span className="text-xs text-darkgray flex items-center gap-1">
                          <LuCalendar className="w-3 h-3" />
                          {
                            formatDate(
                              new Date(chat.updatedAt || chat.createdAt),
                            ).split(",")[0]
                          }
                        </span>
                        <div className="mt-1 flex items-center gap-1">
                          {chat.messages?.some(
                            (m) => m.sentiment === "high",
                          ) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-green-600 text-white">
                              <LuThumbsUp className="w-3 h-3 mr-0.5" />
                            </span>
                          )}
                          {chat.messages?.some(
                            (m) => m.sentiment === "low",
                          ) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-red-600 text-white">
                              <LuThumbsDown className="w-3 h-3 mr-0.5" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-darkgray mt-2 line-clamp-2">
                      {chat.metrics?.totalMessages || 0} messages
                    </p>
                  </div>
                ))
              )}
            </div>
            {chatTypeFilter === "vendor" &&
              pagination &&
              pagination.totalPages > 1 && (
                <div className="p-4 border-t border-lightgray flex items-center justify-between">
                  <div className="text-sm text-darkgray">
                    Page {pagination.page} of {pagination.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="gray-button px-3 py-1 text-sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      className="gray-button px-3 py-1 text-sm"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(pagination.totalPages, prev + 1),
                        )
                      }
                      disabled={currentPage === pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
          </>
          )}
          </div>
        )}

        {selectedChat && chatTypeFilter !== "user" && filterSentiment !== "vendorQuestions" && !isHistoryPending && (
          <div className="w-full md:w-2/3 bg-white rounded border flex flex-col">
            {(() => {
              const chat = chats.find((c) => c._id === selectedChat);
              if (!chat) return null;
              return (
                <>
                  <div className="p-4 border-b border-lightgray flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                      <Button
                        className="md:hidden p-2 rounded-full hover:bg-lightgray transition-colors"
                        onClick={() => setSelectedChat(null)}
                      >
                        <LuArrowLeft className="w-5 h-5" />
                      </Button>
                      <div>
                        <h3>
                          {chat.chatId?.name ||
                            chat.chatId?.propertyId?.address}
                        </h3>
                        {chat.chatId?.participants && (
                          <p className="text-sm text-darkgray mt-1">
                            {getParticipantNames(chat.chatId.participants)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex border-b bg-lightgray">
                    <button
                      className={`px-4 py-2 text-sm font-medium focus:outline-none transition ${
                        activeTab === "messages"
                          ? "border-b-2 border-black text-black"
                          : "text-darkgray"
                      }`}
                      onClick={() => setActiveTab("messages")}
                    >
                      Messages
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium focus:outline-none transition ${
                        activeTab === "history"
                          ? "border-b-2 border-black text-black"
                          : "text-darkgray"
                      }`}
                      onClick={() => setActiveTab("history")}
                    >
                      Full History
                    </button>
                    {chat.buyerChatLink && (
                      <button
                        className={`px-4 py-2 text-sm font-medium focus:outline-none transition ${
                          activeTab === "chatlink"
                            ? "border-b-2 border-black text-black"
                            : "text-darkgray"
                        }`}
                        onClick={() => setActiveTab("chatlink")}
                      >
                        Chat Link
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                    {activeTab === "messages" && (
                      <>
                        <div
                          className="w-full md:w-3/5 p-5 overflow-y-auto"
                          style={{ maxHeight: "calc(100vh - 12rem)" }}
                        >
                          {chatTypeFilter === "vendor" && chat.messages?.some((m) => m.isVendorQuestion) && (
                            <div className="mb-4 flex items-center gap-2">
                              <button
                                onClick={() => setShowOnlyVendorQuestions((v) => !v)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                  showOnlyVendorQuestions
                                    ? "bg-red-600 text-white"
                                    : "bg-lightgray text-black hover:bg-red-100"
                                }`}
                              >
                                <LuMessageSquare className="w-3.5 h-3.5" />
                                Vendor Questions
                                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                                  showOnlyVendorQuestions ? "bg-white text-red-600" : "bg-red-100 text-red-600"
                                }`}>
                                  {chat.messages.filter((m) => m.isVendorQuestion).length}
                                </span>
                              </button>
                            </div>
                          )}
                          <div className="space-y-5">
                            {chat.messages
                              ?.filter((m) => !showOnlyVendorQuestions || m.isVendorQuestion)
                              ?.sort((a, b) => {
                                const aFull = fullHistory?.find(
                                  (msg) => msg._id === a.messageId,
                                );
                                const bFull = fullHistory?.find(
                                  (msg) => msg._id === b.messageId,
                                );

                                if (!aFull?.createdAt && !bFull?.createdAt)
                                  return 0;
                                if (!aFull?.createdAt) return 1;
                                if (!bFull?.createdAt) return -1;

                                return (
                                  new Date(bFull.createdAt) -
                                  new Date(aFull.createdAt)
                                );
                              })
                              ?.map((messageData, index) => {
                                const fullMessage = fullHistory?.find(
                                  (msg) => msg._id === messageData.messageId,
                                );
                                if (!fullMessage) return null;

                                const userMessage =
                                  fullMessage.sender?.userId?.name;

                                return (
                                  <div
                                    key={index}
                                    className={`flex flex-col ${
                                      userMessage?.includes("Sandy")
                                        ? "items-end"
                                        : "items-start"
                                    }`}
                                  >
                                    <div
                                      className={`max-w-[85%] p-4 rounded-lg shadow-sm ${
                                        messageData.sentiment === "high"
                                          ? "bg-green-600 text-white"
                                          : messageData.sentiment === "low" || messageData.isVendorQuestion
                                            ? "bg-red-600 text-white"
                                            : userMessage?.includes("Sandy")
                                              ? "bg-lightgray text-black"
                                              : "bg-black text-white"
                                      }`}
                                    >
                                      <div className="flex justify-between items-center mb-1.5 gap-2">
                                        <div className="flex gap-4">
                                          <span className="text-xs font-medium">
                                            {userMessage?.includes("Sandy")
                                              ? "Sandy"
                                              : fullMessage.sender?.userId
                                                  ?.name || "Buyer"}
                                          </span>
                                          {fullMessage.metadata
                                            ?.messageSource && (
                                            messageData.changeLogId ? (
                                              <span className="self-start flex items-center gap-1 text-xs text-green-600">
                                                Applied
                                              </span>
                                            ) : (
                                            <button
                                              onClick={() =>
                                                handleSuggestImprovement(
                                                  null,
                                                  fullMessage._id,
                                                )
                                              }
                                              disabled={
                                                isSuggesting &&
                                                selectedMessageForSuggestion ===
                                                  fullMessage._id
                                              }
                                              className="self-start flex items-center gap-1 text-xs text-black"
                                            >
                                              <LuWand2 className="w-3 h-3" />
                                              {isSuggesting &&
                                              selectedMessageForSuggestion ===
                                                fullMessage._id
                                                ? "Getting suggestion…"
                                                : "Improve"}
                                            </button>
                                            )
                                          )}
                                        </div>
                                        {messageData.sentiment && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-white text-black">
                                            {getSentimentIcon(
                                              messageData.sentiment,
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm leading-relaxed">
                                        {fullMessage.content?.text ||
                                          "No content"}
                                      </p>
                                      <div className="mt-2 text-right">
                                        <span className="text-xs opacity-70">
                                          {formatDate(fullMessage.createdAt)}
                                        </span>
                                      </div>
                                    </div>
                                    {!userMessage?.includes("Sandy") &&
                                      ((messageData.sentiment === "low" && messageData.triggerSource?.sourceName) || messageData.isVendorQuestion) && (
                                        messageData.changeLogId ? (
                                          <span className="mt-1.5 flex items-center gap-1 text-xs text-green-600">
                                            Improvement applied
                                          </span>
                                        ) : (
                                        <button
                                          onClick={() =>
                                            handleSuggestImprovement(
                                              chat._id,
                                              messageData.messageId,
                                            )
                                          }
                                          disabled={
                                            isSuggesting &&
                                            selectedMessageForSuggestion ===
                                              messageData.messageId
                                          }
                                          className="mt-1.5 flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                                        >
                                          <LuWand2 className="w-3 h-3" />
                                          {isSuggesting &&
                                          selectedMessageForSuggestion ===
                                            messageData.messageId
                                            ? "Getting suggestion…"
                                            : "Suggest Improvement"}
                                        </button>
                                        )
                                      )}
                                  </div>
                                );
                              })}
                          </div>
                        </div>

                        <div
                          className="w-full md:w-2/5 border-t md:border-t-0 md:border-l border-lightgray p-5 overflow-y-auto bg-lightgray"
                          style={{ maxHeight: "calc(100vh - 12rem)" }}
                        >
                          <h4 className="mb-4">Sentiment Analysis</h4>

                          {/* Overall sentiment + progress bar */}
                          <div className="mb-4 bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex justify-between items-center mb-3">
                              <span className="text-sm font-medium">
                                Overall
                              </span>
                              <div className="flex items-center gap-1">
                                {chat.overallSentiment === "positive" ? (
                                  <>
                                    <LuThumbsUp className="w-4 h-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-600">
                                      Positive
                                    </span>
                                  </>
                                ) : chat.overallSentiment === "negative" ? (
                                  <>
                                    <LuThumbsDown className="w-4 h-4 text-red-600" />
                                    <span className="text-sm font-medium text-red-600">
                                      Negative
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-sm font-medium text-darkgray">
                                    Neutral
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="w-full bg-lightgray rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full transition-all duration-500"
                                style={{
                                  width: `${
                                    chat.messages?.length > 0
                                      ? (chat.messages.filter(
                                          (m) => m.sentiment === "high",
                                        ).length /
                                          chat.messages.length) *
                                        100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Chat stats */}
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                              <p className="text-xs text-darkgray">Total</p>
                              <p className="font-semibold text-sm mt-0.5">
                                {chat.metrics?.totalMessages || 0}
                              </p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                              <p className="text-xs text-darkgray">Positive</p>
                              <p className="font-semibold text-sm mt-0.5 text-green-600">
                                {chat.metrics?.positiveCount || 0}
                              </p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                              <p className="text-xs text-darkgray">Negative</p>
                              <p className="font-semibold text-sm mt-0.5 text-red-600">
                                {chat.metrics?.negativeCount || 0}
                              </p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm text-center">
                              <p className="text-xs text-darkgray">Issues</p>
                              <p className="font-semibold text-sm mt-0.5 text-orange-600">
                                {chat.metrics?.issuesIdentified || 0}
                              </p>
                            </div>
                          </div>

                          {/* AI analysis — whatWentWrong / whatWentRight */}
                          {chat.analysisResults ? (
                            <div className="space-y-3">
                              {chat.analysisResults.analyzedAt && (
                                <p className="text-xs text-darkgray">
                                  Analysed{" "}
                                  {moment(
                                    chat.analysisResults.analyzedAt,
                                  ).fromNow()}
                                </p>
                              )}

                              {/* What went wrong */}
                              {chat.analysisResults.whatWentWrong?.length >
                                0 && (
                                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1">
                                    <LuThumbsDown className="w-3 h-3" />
                                    What Went Wrong
                                  </p>
                                  <ul className="space-y-1.5">
                                    {chat.analysisResults.whatWentWrong.map(
                                      (item, i) => (
                                        <li
                                          key={i}
                                          className="flex items-start gap-1.5"
                                        >
                                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                          <span className="text-xs text-red-900">
                                            {item}
                                          </span>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}

                              {/* What went right */}
                              {chat.analysisResults.whatWentRight?.length >
                                0 && (
                                <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                                    <LuThumbsUp className="w-3 h-3" />
                                    What Went Right
                                  </p>
                                  <ul className="space-y-1.5">
                                    {chat.analysisResults.whatWentRight.map(
                                      (item, i) => (
                                        <li
                                          key={i}
                                          className="flex items-start gap-1.5"
                                        >
                                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                          <span className="text-xs text-green-900">
                                            {item}
                                          </span>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}

                              <Button
                                className="gray-button w-full text-xs flex items-center justify-center gap-1"
                                onClick={startAnalysis}
                                disabled={isAnalyzing}
                                loading={isAnalyzing}
                              >
                                Regenerate Analysis
                              </Button>
                            </div>
                          ) : (
                            <div className="bg-white rounded-lg p-4 text-center border border-lightgray">
                              <p className="text-xs text-darkgray mb-3">
                                No AI analysis yet. Run it to see what went
                                right and wrong.
                              </p>
                              <Button
                                className="black-button w-full text-xs flex items-center justify-center gap-1"
                                onClick={startAnalysis}
                                disabled={isAnalyzing}
                                loading={isAnalyzing}
                              >
                                {isAnalyzing ? "Analysing…" : "Run Analysis"}
                              </Button>
                            </div>
                          )}

                          {/* Summary */}
                          {chat.summary && (
                            <div className="mt-4 prose prose-sm max-w-none bg-white p-4 rounded-lg shadow-sm">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: chat.summary,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {activeTab === "history" && (
                      <div className="w-full p-5 overflow-y-auto">
                        <h4 className="mb-4 font-semibold">
                          Full Message History
                        </h4>
                        {Array.isArray(fullHistory) &&
                        fullHistory.length > 0 ? (
                          <div className="space-y-4">
                            {fullHistory
                              ?.sort(
                                (a, b) =>
                                  new Date(b.createdAt) - new Date(a.createdAt),
                              )
                              ?.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="bg-lightgray rounded-lg p-4 shadow-sm flex flex-col gap-2 border border-lightgray"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="font-semibold text-black">
                                        {item.sender?.userId?.name || "Sandy"}
                                      </span>
                                      {/* Proactive improvement — only on Sandy messages with a messageSource */}
                                      {item.metadata?.messageSource && (() => {
                                        const logMsg = chat.messages?.find(
                                          (m) => m.messageId === item._id || m.messageId?.toString() === item._id?.toString()
                                        );
                                        return logMsg?.changeLogId ? (
                                          <span className="self-start flex items-center gap-1 text-xs text-green-600">
                                            Applied
                                          </span>
                                        ) : (
                                          <button
                                            onClick={() =>
                                              handleSuggestImprovement(
                                                null,
                                                item._id,
                                              )
                                            }
                                            disabled={
                                              isSuggesting &&
                                              selectedMessageForSuggestion ===
                                                item._id
                                            }
                                            className="self-start flex items-center gap-1 text-xs text-black"
                                          >
                                            <LuWand2 className="w-3 h-3" />
                                            {isSuggesting &&
                                            selectedMessageForSuggestion ===
                                              item._id
                                              ? "Getting suggestion…"
                                              : "Improve"}
                                          </button>
                                        );
                                      })()}
                                      <span className="px-2 py-0.5 rounded-full text-xs bg-white text-darkergray border border-lightgray ml-auto">
                                        {item.createdAt
                                          ? new Date(
                                              item.createdAt,
                                            ).toLocaleString("en-AU", {
                                              month: "short",
                                              day: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                          : "-"}
                                      </span>
                                    </div>
                                    <p className="text-sm text-darkergray break-words whitespace-pre-line">
                                      {item.content?.text || "No content"}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="text-center text-darkgray py-8">
                            No full history available for this chat.
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === "chatlink" && (
                      <div className="w-full p-5 overflow-y-auto">
                        <h4 className="mb-4 font-semibold">Chat Link</h4>
                        {chat.buyerChatLink ? (
                          <div className="bg-lightgray p-4 rounded-lg shadow-sm">
                            <Button
                              className="mt-4 black-button"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  chat.buyerChatLink,
                                );
                              }}
                            >
                              Copy Link
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center text-darkgray py-8">
                            No chat link available for this chat.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}
        {selectedChat && isHistoryPending && filterSentiment !== "vendorQuestions" && (
          <div className="w-full md:w-2/3 bg-white rounded border flex flex-col">
            <PageLoader />
          </div>
        )}
        {!selectedChat && chatTypeFilter !== "user" && filterSentiment !== "vendorQuestions" && (
          <div className="w-full md:w-2/3 bg-white rounded-lg shadow hidden md:flex items-center justify-center p-12">
            <div className="text-center">
              <LuMessageSquare className="w-16 h-16 text-mediumgray mx-auto mb-4" />
              <h3 className="text-xl font-medium text-darkergray">
                Select a chat
              </h3>
              <p className="text-darkgray mt-2 max-w-md">
                Choose a chat from the list to view the message history and
                sentiment analysis
              </p>
            </div>
          </div>
        )}
        {filterSentiment === "vendorQuestions" && (
              <>
                {isVendorQuestionsPending ? (
                  <div className="p-8 text-center text-darkgray text-sm">Loading…</div>
                ) : vendorQuestions.length === 0 ? (
                  <div className="p-8 text-center text-darkgray text-sm">
                    No vendor questions found. They appear once the cron has run on vendorGroup chats.
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                  <div className="divide-y divide-lightgray max-h-[calc(100vh-14rem)] overflow-y-auto">
                    {vendorQuestions?.sort((a, b) => b.askedAt - a.askedAt).map((q) => (
                      <div key={`${q.logId}-${q.messageId}`} className="p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-darkergray break-words">
                              {q.questionText || <span className="italic text-darkgray">No text</span>}
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-darkgray">
                              <span>{q.chatName}</span>
                              {q.propertyAddress && q.propertyAddress !== q.chatName && (
                                <span className="text-mediumgray">{q.propertyAddress}</span>
                              )}
                              {q.askedAt && (
                                <span>{moment(q.askedAt).format("D MMM YYYY, h:mm a")}</span>
                              )}
                              {q.marketWeek === "beforeLive" ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-lightgray text-darkgray font-medium">
                                  Before go live
                                </span>
                              ) : q.marketWeek != null ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                                  Week {q.marketWeek} on market
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            {q.isFixed ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">
                                Fixed
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-medium">
                                Not Fixed
                              </span>
                            )}
                            {q.isFixed ? (
                              <button
                                onClick={() => {
                                  setSelectedImprovementCard(q);
                                  setShowImprovementDetailModal(true);
                                }}
                                className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1 transition-colors"
                              >
                                <LuWand2 className="w-3 h-3" />
                                Improvement applied
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSuggestImprovement(q.logId, q.messageId)}
                                disabled={isSuggesting && selectedMessageForSuggestion === q.messageId}
                                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                              >
                                <LuWand2 className="w-3 h-3" />
                                {isSuggesting && selectedMessageForSuggestion === q.messageId
                                  ? "Getting suggestion…"
                                  : "Suggest Improvement"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                  </div>
                  {vendorQuestionsPagination && vendorQuestionsPagination.totalPages > 1 && (
                  <div className="p-4 border-t border-lightgray flex items-center justify-between">
                    <Button
                      className="gray-button text-xs"
                      disabled={vendorQuestionsPage <= 1}
                      onClick={() => setVendorQuestionsPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-darkgray">
                      Page {vendorQuestionsPage} of {vendorQuestionsPagination.totalPages}
                    </span>
                    <Button
                      className="gray-button text-xs"
                      disabled={vendorQuestionsPage >= vendorQuestionsPagination.totalPages}
                      onClick={() => setVendorQuestionsPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
                  </div>
                )}
                
              </>
            )}
      </div>

      {/* Suggestion modal — triggered from low-sentiment Sandy messages */}
      <Modal
        isOpen={showSuggestionModal}
        onClose={() => {
          setShowSuggestionModal(false);
          setSuggestionData(null);
          setSelectedMessageForSuggestion(null);
          setSelectedLogForSuggestion(null);
        }}
        title="Suggest Improvement"
        className="max-w-4xl"
      >
        {isSuggesting ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <i className="fa-solid fa-spinner animate-spin text-2xl text-darkgray"></i>
            <p className="text-darkgray text-sm">Generating suggestion…</p>
            <p className="text-darkgray text-xs">This may take a moment.</p>
          </div>
        ) : !suggestionData ? (
          <div className="py-10 text-center text-darkgray text-sm">
            No suggestion data available.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Source badge */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-darkgray">Source:</span>
              <span className="px-2 py-0.5 rounded-full bg-black text-white text-xs">
                {suggestionData.triggerSource?.sourceName}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-lightgray text-darkergray text-xs">
                {suggestionData.triggerSource?.sourceModel}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-lightgray text-darkergray text-xs">
                field: {suggestionData.targetField}
              </span>
            </div>

            {/* Improvement points */}
            {suggestionData.improvementPoints?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wide">
                  Why this needs improvement
                </p>
                <ul className="space-y-1.5">
                  {suggestionData.improvementPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0"></span>
                      <span className="text-sm text-amber-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Side-by-side current vs suggested */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-darkgray uppercase tracking-wide mb-2">
                  Current
                </p>
                <div className="bg-lightgray rounded-lg p-4 text-sm text-darkergray whitespace-pre-wrap leading-relaxed min-h-[120px]">
                  {suggestionData.currentContent || (
                    <span className="italic text-darkgray">No content</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
                  Suggested
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-darkergray leading-relaxed min-h-[120px]">
                  {suggestionData.suggestedContent ? (
                    <EditableField
                      value={editedSuggestedContent}
                      onSave={(val) => setEditedSuggestedContent(val)}
                      editing={true}
                      className="w-full"
                    />
                  ) : (
                    <span className="italic text-darkgray">No suggestion</span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t border-lightgray">
              <Button
                className="gray-button"
                onClick={() => {
                  setShowSuggestionModal(false);
                  setSuggestionData(null);
                  setSelectedMessageForSuggestion(null);
                  setSelectedLogForSuggestion(null);
                }}
              >
                Dismiss
              </Button>
              <Button
                className="black-button flex items-center gap-2"
                onClick={handleApplyImprovement}
                disabled={isApplying}
                loading={isApplying}
              >
                Apply Improvement
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Read-only modal — view what was applied for a Fixed vendor question */}
      <Modal
        isOpen={showImprovementDetailModal}
        onClose={() => {
          setShowImprovementDetailModal(false);
          setSelectedImprovementCard(null);
        }}
        title="Applied Improvement"
        className="max-w-2xl"
      >
        {selectedImprovementCard && (
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-xs font-semibold text-darkgray uppercase tracking-wide mb-1">
                Vendor Question
              </p>
              <p className="text-sm text-darkergray">
                {selectedImprovementCard.questionText || <span className="italic text-darkgray">No text</span>}
              </p>
            </div>
            {(() => {
              let points = [];
              try {
                points = JSON.parse(selectedImprovementCard.improvement?.suggestionText || "[]");
              } catch {}
              return points.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold text-darkgray uppercase tracking-wide mb-2">
                    Why this was improved
                  </p>
                  <ul className="flex flex-col gap-1.5">
                    {points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-darkergray">
                        <span className="text-green-600 mt-0.5 flex-shrink-0">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null;
            })()}
            {selectedImprovementCard.improvement?.newContent && (
              <div>
                <p className="text-xs font-semibold text-darkgray uppercase tracking-wide mb-2">
                  Applied Content
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-darkergray whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto">
                  {selectedImprovementCard.improvement.newContent}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-4 text-xs text-darkgray pt-3 border-t border-lightgray">
              {selectedImprovementCard.improvement?.sourceName && (
                <span>
                  <span className="font-medium">Prompt: </span>
                  {selectedImprovementCard.improvement.sourceName}
                </span>
              )}
              {selectedImprovementCard.improvement?.appliedAt && (
                <span>
                  <span className="font-medium">Applied: </span>
                  {moment(selectedImprovementCard.improvement.appliedAt).format("D MMM YYYY, h:mm a")}
                </span>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
